import BayunError from "../constants/BayunError";
import SharedPreferences from "../constants/SharedPreferences";
import {
  aeadDecryptWithAssociatedData,
  aeadEncryptWithAssociatedData,
  generateAesKey,
} from "../crypto/AuthenticatedEncryption";
import {
  exportEccPrivateKey,
  exportEccPublicKey,
  generateEccSigningKeyPair,
} from "../crypto/EccEncryption";
import { updateAndSaveStatistics } from "../manager/StatisticsManager";
import {
  createGroup,
  getMemberLockBox,
  getMemberPublicKey,
  getMyGroups,
  addNewParticipantToGroupApiCall,
  leaveGroup,
  getMemberPublicKeys,
  addNewParticipantsToGroupApiCall,
  getUnjoinedGroupsAPI,
  getOrgEncryptedGroupKey,
  joinPublicGroupAPI,
  getGroupById,
  getMemberClientData,
} from "./BayunAPIManager";
import {
  createLMSGroupKey,
  getErrorMessage,
  getFromStorage,
  logApiResponse,
  saveInStorage,
  getEncryptionCurveFromStorage,
  getSigningCurveFromStorage,
} from "../util/Utility";
import { encryptAsymmetric } from "../util/AsymmetricEncryption";
import {
  appendSignatureAndMetadata,
  getMemberPublicKeyContext,
  getGroupKeyContext,
  getGroupKeyKekContext,
  getGroupPrivateKeyContext,
  getGroupPrivateKeyKekContext,
  getGroupPublicKeyContext,
  getGroupTypeContext,
  getMessageFromSignatureString,
  retrieveAndverifyLastSignature,
  signData,
  signDataAndAppendSignatureWithMetadata,
  verifyMemberPublicKey,
  verifyMessageForPublicKeyTag,
} from "../util/SignatureUtil";
import SigningPublicKeyTags from "../constants/SigningPublicKeyTags";
import { serializeThrift } from "../thrift/ThriftFormatter";
import { SignatureMetadata } from "../thrift/SignatureMetadata_types";
import { GroupType } from "../BayunCore";
import Constants from "../constants/Constants";
import { encodeKey, decodeKey } from "../crypto/PBKDF2";
import { asRawKey } from "../crypto/KeyMaterial";

async function getMemberSigningPrivateKey(sessionId) {
  const key = await getFromStorage(sessionId, SharedPreferences.MEMBER_SIGNING_PRIVATE_KEY);
  if (!key) {
    throw new Error("Member signing private key is required.");
  }
  return key;
}

async function isOrgAdminApprovalPolicy(sessionId) {
  return (
    (await getFromStorage(sessionId, SharedPreferences.KEY_VALIDATION_POLICY)) ===
    Constants.KEY_VALIDATION_POLICY.ORG_ADMIN_APPROVAL
  );
}

async function assertAddMemberInitiationAllowed(sessionId, orgName) {
  if (!(await isOrgAdminApprovalPolicy(sessionId))) {
    return;
  }

  const memberStatus = await getFromStorage(
    sessionId,
    SharedPreferences.MEMBER_STATUS,
  );
  if (memberStatus === Constants.MEMBER_STATUS.AUTO_APPROVED) {
    throw BayunError.AUTO_APPROVED_MEMBER_CANNOT_ADD_TO_GROUP;
  }

  const loggedInOrgName = (
    await getFromStorage(sessionId, SharedPreferences.ORG_NAME)
  )?.toLowerCase();
  if (orgName !== loggedInOrgName) {
    throw BayunError.CANNOT_ADD_MEMBER_FROM_OTHER_ORG;
}
}

/**
 * Whether this member may not create groups: auto-approved, under a policy that requires an admin.
 *
 * MEMBER_STATUS is written into the session at login and by nothing else, so once an admin approves
 * a member the stored value stays "AutoApproved" for the life of that session - a page reload
 * restores the same storage and does not re-read it. Blocking on the stored value alone therefore
 * kept refusing group creation to members who had already been approved, until they logged out and
 * back in.
 *
 * The stored value is trusted to allow, never to refuse: only when it says to block is the lockbox
 * re-read, which rewrites MEMBER_STATUS from the server. That keeps the common path free of a round
 * trip and costs one call only where the alternative was a wrong answer. A member who really is
 * auto-approved is refused either way, and the server enforces this independently in
 * GroupController - this check exists to fail early with a clearer error.
 *
 * @param {String} sessionId id of the session in question.
 * @returns {Promise<Boolean>} true when group creation must be refused.
 */
const isBlockedFromCreatingGroups = async (sessionId) => {
  const blockedByStoredState = async () =>
    (await getFromStorage(sessionId, SharedPreferences.MEMBER_STATUS)) ===
      Constants.MEMBER_STATUS.AUTO_APPROVED &&
    (await getFromStorage(sessionId, SharedPreferences.KEY_VALIDATION_POLICY)) ===
      Constants.KEY_VALIDATION_POLICY.ORG_ADMIN_APPROVAL;

  if (!(await blockedByStoredState())) {
    return false;
  }

  const orgMemberId = await getFromStorage(
    sessionId,
    SharedPreferences.ORG_MEMBER_ID,
  );
  if (!orgMemberId) {
    return true;
  }

  try {
    await getMemberLockBox(sessionId, orgMemberId);
  } catch (error) {
    // Refusing is what the stored state already said; a failed refresh is no reason to let the
    // call through, and the server would refuse it anyway.
    console.log("Could not refresh member status before creating a group:", error);
    return true;
  }

  return blockedByStoredState();
};

/**
 * The function creates a new private group with Bayun.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupName The name of the group.
 * @returns {Promise} the response of which contains an object with the groupId and groupkey.
 */
export const createNewGroup = async (sessionId, groupName, groupType) => {
  try {
    // TODO: Create a new API to check if the member is authorized to create groups
    if (await isBlockedFromCreatingGroups(sessionId)) {
      throw BayunError.ERROR_MEMBER_NOT_AUTHORIZED_TO_CREATE_GROUPS;
    }

    let groupTypeString = "";
    if (groupType === GroupType.PRIVATE) {
      groupTypeString = "Private";
    } else if (groupType === GroupType.PUBLIC) {
      groupTypeString = "Org-Public";
    }

    let memberKey = asRawKey(
      await getFromStorage(sessionId, SharedPreferences.MEMBER_KEY),
    );

    const orgKey = asRawKey(
      await getFromStorage(sessionId, SharedPreferences.ORG_KEY),
    );
    const orgMemberId = await getFromStorage(
      sessionId,
      SharedPreferences.ORG_MEMBER_ID,
    );
    const orgName = await getFromStorage(sessionId, SharedPreferences.ORG_NAME);
    const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);
  const signingCurve = await getSigningCurveFromStorage(sessionId);


    if (!memberKey || !orgKey) {
      // get the memberLockBox and decrypt the key
      // calling memeber client data to save statistics key also 
      await getMemberLockBox(sessionId, orgMemberId);
      memberKey = asRawKey(
        await getFromStorage(sessionId, SharedPreferences.MEMBER_KEY),
      );
    }

    const memberPublicKey = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_PUBLIC_KEY,
    );

    // create new group key
    const groupKey = await generateAesKey();
    const groupKeyEncoded = encodeKey(groupKey);

    // create lmsGroupKey
    const lmsGroupKey = await createLMSGroupKey(groupKey, orgName, orgMemberId);

    // create groupInfo key
    const groupInfoKey = await generateAesKey();
    const groupInfoKeyEncoded = encodeKey(groupInfoKey);
    const baseId = orgName + orgMemberId;

    // encrypt groupName and groupType with groupInfoKey
    const encryptedGroupType = await aeadEncryptWithAssociatedData(
      groupTypeString,
      baseId,
      groupInfoKey,
    );

    // sign encrypted GroupType
    const memberSigningPrivateKey = await getMemberSigningPrivateKey(sessionId);

    let signedGroupType = await signData(
      encryptedGroupType,
      memberSigningPrivateKey,
      await getGroupTypeContext(sessionId),
      signingCurve,
    );

    if (!signedGroupType) {
      console.log("GroupType Signature could not be generated.");
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    // append signature metadata to the signature on the groupType of the group
    const signatureMetadataObj = new SignatureMetadata();
    signatureMetadataObj.context = await getGroupTypeContext(sessionId);
    signatureMetadataObj.signatureVerificationKey =
      SigningPublicKeyTags.GROUP_CREATOR_PUBLIC_KEY_TAG;

    const signatureMetadata = serializeThrift(signatureMetadataObj);
    signedGroupType = signedGroupType + "$" + signatureMetadata;

    let encryptedGroupName = "";
    if (groupName) {
      encryptedGroupName = await aeadEncryptWithAssociatedData(
        groupName,
        baseId,
        groupInfoKey,
      );
    }

    // encrypt groupInfoKey using admin public key
    const adminEncryptedGroupInfo = await encryptAsymmetric(
      groupInfoKeyEncoded,
      await getFromStorage(sessionId, SharedPreferences.ADMIN_PUBLIC_KEY),
      baseId,
      encryptionCurve,
    );

    let adminEncryptedGroupInfoKey = "";
    let adminEncryptedGroupInfo_kek = "";

    if (adminEncryptedGroupInfo) {
      adminEncryptedGroupInfoKey = adminEncryptedGroupInfo.encryptedText;
      adminEncryptedGroupInfo_kek = adminEncryptedGroupInfo.keyEncryptionKey;
    }

    // encrypt group key with member key for both types of a group
    const memberEncGroupKey = await encryptAsymmetric(
      groupKeyEncoded,
      memberPublicKey,
      orgMemberId,
      encryptionCurve,
    );

    let memberEncGroupKey_key = "";
    let memberEncGroupKey_kek = "";

    if (memberEncGroupKey) {
      memberEncGroupKey_key = memberEncGroupKey.encryptedText;
      memberEncGroupKey_kek = memberEncGroupKey.keyEncryptionKey;
    }

    // sign Group Key using member signing key
    const signedGroupKey = await signData(
      memberEncGroupKey_key,
      memberSigningPrivateKey,
      await getGroupKeyContext(sessionId, groupName),
      signingCurve,
    );

    // Create the group signing key pair before the backdoor copies below, which need the private
    // key itself. It used to be generated further down, so the backdoor copy could only be built
    // from something that already existed - and what it was given was the encrypted symmetric
    // group key under a variable named for a private key. An admin adding a participant sources
    // that copy, so the new participant received an encrypted group key where a signing private
    // key was expected, and joining failed decoding it as PEM.
    const groupSigningKeyPair = await generateEccSigningKeyPair(signingCurve);
    let groupSigningPublicKey = await exportEccPublicKey(
      groupSigningKeyPair.publicKey,
    );
    const groupSigningPrivateKey = await exportEccPrivateKey(
      groupSigningKeyPair.privateKey,
    );

    const backdoorPublicKey = await getFromStorage(
      sessionId,
      SharedPreferences.BACKDOOR_PUBLIC_KEY,
    );

    // Encrypt the group signing private key itself with the backdoorPublicKey. Wrapping the
    // already-encrypted value meant a reader who decrypted this copy was left holding ciphertext
    // rather than a key.
    let backdoorEncryptedGroupPrivateKey = "";
    let backdoorEncryptedGroupPrivateKey_kek = "";

    const backdoorEncryptedGroupPrivateKeyResult = await encryptAsymmetric(
      groupSigningPrivateKey,
      backdoorPublicKey,
      orgMemberId,
      encryptionCurve,
    );

    if (backdoorEncryptedGroupPrivateKeyResult) {
      backdoorEncryptedGroupPrivateKey =
        backdoorEncryptedGroupPrivateKeyResult.encryptedText;
      backdoorEncryptedGroupPrivateKey_kek =
        backdoorEncryptedGroupPrivateKeyResult.keyEncryptionKey;
    }

    // encrypt the groupKey with the backdoorPublicKey of the creator
    let backdoorEncryptedGroupKey = "";
    let backdoorEncryptedGroupKey_kek = "";

    const backdoorEncryptedGroupKeyResult = await encryptAsymmetric(
      groupKeyEncoded,
      backdoorPublicKey,
      orgMemberId,
      encryptionCurve,
    );

    if (backdoorEncryptedGroupKeyResult) {
      backdoorEncryptedGroupKey = backdoorEncryptedGroupKeyResult.encryptedText;
      backdoorEncryptedGroupKey_kek =
        backdoorEncryptedGroupKeyResult.keyEncryptionKey;
    }

    // encrypt the signing private key with member public key
    let groupSigningPrivateKeyMemberEncrypted = "";
    let groupSigningPrivateKeyMemberEncrypted_kek = "";

    const groupSigningPrivateKeyMemberEncryptedResult = await encryptAsymmetric(
      groupSigningPrivateKey,
      memberPublicKey,
      orgMemberId,
      encryptionCurve,
    );

    if (groupSigningPrivateKeyMemberEncryptedResult) {
      groupSigningPrivateKeyMemberEncrypted =
        groupSigningPrivateKeyMemberEncryptedResult.encryptedText;
      groupSigningPrivateKeyMemberEncrypted_kek =
        groupSigningPrivateKeyMemberEncryptedResult.keyEncryptionKey;
    }

    // sign Group Private Key using member signing key
    const signedGroupPrivateKey = await signData(
      groupSigningPrivateKeyMemberEncrypted,
      memberSigningPrivateKey,
      await getGroupPrivateKeyContext(sessionId, groupName),
      signingCurve,
    );

    // encrypt groupInfoKey using lmsGroupKey
    const lmsEncryptedGroupInfoKey = await aeadEncryptWithAssociatedData(
      groupInfoKeyEncoded,
      "lms" + baseId,
      lmsGroupKey,
    );

    //TODO: this condition is only for ECC encryptionType
    if (true) {
      // sign memberEncGroupKey_kek using member signing key
      const signedMemberPubKeyEncGroupKeyKEK = await signData(
        memberEncGroupKey_kek,
        memberSigningPrivateKey,
        await getGroupKeyKekContext(sessionId, orgMemberId),
        signingCurve,
      );

      if (!signedMemberPubKeyEncGroupKeyKEK) {
        console.log(
          "MemberPubKeyEncGroupKeyKEK Signature could not be generated.",
        );
        throw BayunError.ERROR_AUTHENTICATION_FAILED;
      }

      memberEncGroupKey_kek = appendSignatureAndMetadata(
        memberEncGroupKey_kek,
        signedMemberPubKeyEncGroupKeyKEK,
        SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
        await getGroupKeyKekContext(sessionId, orgMemberId),
      );

      // sign group_private_key_kek using member signing key
      const signedGroupPrivateKeyKek = await signData(
        groupSigningPrivateKeyMemberEncrypted_kek,
        memberSigningPrivateKey,
        await getGroupPrivateKeyKekContext(sessionId, orgName, orgMemberId),
        signingCurve,
      );

      if (!signedGroupPrivateKeyKek) {
        console.log("GroupPrivateKeyKek Signature could not be generated.");
        throw BayunError.ERROR_AUTHENTICATION_FAILED;
      }

      groupSigningPrivateKeyMemberEncrypted_kek = appendSignatureAndMetadata(
        groupSigningPrivateKeyMemberEncrypted_kek,
        signedGroupPrivateKeyKek,
        SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
        await getGroupPrivateKeyKekContext(sessionId, orgName, orgMemberId),
      );
    }

    // sign groupSigningPublicKey (First Layer) using groupSigningPrivateKey
    const signedGroupSigningPublicKey = await signData(
      groupSigningPublicKey,
      groupSigningPrivateKey,
      await getGroupPublicKeyContext(sessionId, groupName),
      signingCurve,
    );

    if (!signedGroupSigningPublicKey) {
      console.log("GroupSigningPublicKey Signature could not be generated.");
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    const intermediateSignedGroupPublicKey = appendSignatureAndMetadata(
      groupSigningPublicKey,
      signedGroupSigningPublicKey,
      SigningPublicKeyTags.GROUP_PUBLIC_KEY_TAG,
      await getGroupPublicKeyContext(sessionId, groupName),
    );

    // sign groupPublicKey (Second Layer) using creator’s member signing key and use first signature on groupPublicKey as context
    const signatureContext =
      (await getGroupPublicKeyContext(sessionId, groupName)) +
      "$" +
      signedGroupSigningPublicKey;
    const memberPrivKeySignedGroupPublicKey = await signData(
      groupSigningPublicKey,
      memberSigningPrivateKey,
      signatureContext,
      signingCurve,
    );

    if (!memberPrivKeySignedGroupPublicKey) {
      console.log(
        "memberPrivKeySignedGroupPublicKey Signature could not be generated.",
      );
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    groupSigningPublicKey = appendSignatureAndMetadata(
      intermediateSignedGroupPublicKey,
      memberPrivKeySignedGroupPublicKey,
      SigningPublicKeyTags.GROUP_CREATOR_PUBLIC_KEY_TAG,
      signatureContext,
    );

    // encrypt signingPrivateKey with orgKey
    let signingPrivateKeyOrgEncrypted = "";
    let orgKeyEncryptedGroupKey = "";

    // TODO: remove this for private groups. (IMPORTANT)
    if (groupType == GroupType.PUBLIC) {
      const creatorOrgName = await getFromStorage(
        sessionId,
        SharedPreferences.ORG_NAME,
      );
      const creatorOrgMemberId = await getFromStorage(
        sessionId,
        SharedPreferences.ORG_MEMBER_ID,
      );
      const baseId = creatorOrgName + creatorOrgMemberId;

      // encrypt Group Key using Org Key
      orgKeyEncryptedGroupKey = await aeadEncryptWithAssociatedData(
        groupKeyEncoded,
        baseId,
        orgKey,
      );

      // encrypt groupPrivateKey with Org Key
      signingPrivateKeyOrgEncrypted = await aeadEncryptWithAssociatedData(
        groupSigningPrivateKey,
        (await getFromStorage(sessionId, SharedPreferences.ORG_NAME)) +
        (await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID)),
        orgKey,
      );
    }

    // verifying for null properties
    if (
      !backdoorEncryptedGroupPrivateKey ||
      !backdoorEncryptedGroupPrivateKey_kek ||
      !backdoorEncryptedGroupKey ||
      !backdoorEncryptedGroupKey_kek
    ) {
      console.log("Backdoor encrypted keys are null");
    }

    // call the create group API
    const createGroupResponse = await createGroup(
      sessionId,
      encryptedGroupName,
      memberEncGroupKey_key,
      memberEncGroupKey_kek,
      groupSigningPublicKey,
      groupSigningPrivateKeyMemberEncrypted,
      groupSigningPrivateKeyMemberEncrypted_kek,
      signingPrivateKeyOrgEncrypted,
      adminEncryptedGroupInfoKey,
      adminEncryptedGroupInfo_kek,
      lmsEncryptedGroupInfoKey,
      encodeKey(lmsGroupKey),
      signedGroupType,
      signedGroupKey,
      signedGroupPrivateKey,
      encryptedGroupType,
      orgKeyEncryptedGroupKey,
      backdoorEncryptedGroupPrivateKey,
      backdoorEncryptedGroupPrivateKey_kek,
      backdoorEncryptedGroupKey,
      backdoorEncryptedGroupKey_kek,
    );

    const groupId = createGroupResponse.groupId;

    // save the groupKey in the cache
    const savedGroupKeysString = await getFromStorage(
      sessionId,
      SharedPreferences.JOINED_GROUP_KEYS,
    );
    let savedGroupKeys;

    if (savedGroupKeysString) {
      savedGroupKeys = JSON.parse(savedGroupKeysString);
    } else {
      savedGroupKeys = [];
    }

    savedGroupKeys.push({
      id: groupId,
      key: groupKeyEncoded,
      signingPrivateKey: groupSigningPrivateKey,
      signingPublicKey: groupSigningPublicKey,
      signedSigningPublicKey: createGroupResponse.groupPublicKey,
      creatorOrgName: orgName,
      creatorOrgMemberId: orgMemberId,
      groupName: groupName,
    });
    await saveInStorage(
      sessionId,
      SharedPreferences.JOINED_GROUP_KEYS,
      JSON.stringify(savedGroupKeys),
    );

    return {
      groupId,
      groupKey,
    };
  } catch (error) {
    console.log("error : ", error);
    if (error.response) {
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    } else {
      throw error;
    }
  }
};

/**
 * This function checks if the gorupKey exists in local storage.
 * If so, returns the matching group key, else call the get my groups API.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId Id of the group whose key is to be returned.
 */
export const getGroupKeyFromId = async (sessionId, groupId) => {
  // check for groupinfo in cache
  let groupInfo;
  try {
    groupInfo = await getGroupInfoFromCache(sessionId, groupId, true);
  } catch (error) {
    console.error(error);
    console.log("Retrying");
  }

  // if (groupInfo.key != null) {
  //   console.log("groupInfo"+groupInfo+"RETURNING");
  //   return groupInfo;
  // }

  if (groupInfo) {
    return groupInfo;
  }

  // if the flow comes here, that means the groupKey was not found in cache
  try {
    let memberKey = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_KEY,
    );
    const orgMemberId = await getFromStorage(
      sessionId,
      SharedPreferences.ORG_MEMBER_ID,
    );

    if (!memberKey) {
      // get the memberLockBox and decrypt the key
      await getMemberLockBox(sessionId, orgMemberId);
      memberKey = await getFromStorage(sessionId, SharedPreferences.MEMBER_KEY);
    }

    // call the API to get joined groups of the member
    await getGroupById(sessionId, groupId);

    // check for groupinfo in cache
    const groupInfo = await getGroupInfoFromCache(sessionId, groupId, true);
    if (groupInfo) {
      return groupInfo;
    } else {
      // This is executed when the mentioned groupId is not in the list of joined groups.
      throw BayunError.ERROR_MEMBER_DOESNT_BELONG_TO_GROUP;
    }
  } catch (error) {
    console.log(error);
    if (error.response) {
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    } else {
      throw error;
    }
  }
};

/**
 * This method returns the joined groups of an member.
 *
 * @param {String} sessionId id of the session in question.
 */
export const getJoinedGroups = async (sessionId) => {
  await getMyGroups(sessionId);
  const myGroups = [];

  const savedGroupKeysString = await getFromStorage(
    sessionId,
    SharedPreferences.JOINED_GROUP_KEYS,
  );

  if (savedGroupKeysString) {
    const savedGroupKeys = JSON.parse(savedGroupKeysString);

    // check if the cache has the key for the given groupId
    if (savedGroupKeys) {
      for (let i = 0; i < savedGroupKeys.length; i++) {
        const group = savedGroupKeys[i];
        myGroups.push({
          groupId: group.id,
          groupName: group.groupName,
          groupType: group.groupType,
        });
      }
    }
  }

  return myGroups;
};

/**
 * This method returns the list of unjoined public groups for an member.
 *
 * @param {String} sessionId id of the session in question.
 */
export const getUnjoinedGroupsForMember = async (sessionId) => {
  const unjoinedGroups = await getUnjoinedGroupsAPI(sessionId);
  const unjoinedGroupsCache = [];
  const unjoinedGroupsResult = [];

  // if orgKey is not found, call the getMemberLockbox API
  if (!(await getFromStorage(sessionId, SharedPreferences.ORG_KEY))) {
    await getMemberLockBox(
      sessionId,
      await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
    );
    return await getUnjoinedGroupsForMember(sessionId);
  }

  for (let index = 0; index < unjoinedGroups.length; index++) {
    const group = unjoinedGroups[index];
    const creatorMemberPublicKey = group.creatorMemberPublicKey;
    const groupPublicKey = group.groupPublicKey;
    let encGroupKey = group.groupKey;
    const creatorOrgName = group.creatorOrgName;
    const creatorOrgMemberId = group.creatorOrgMemberId;
    const encGroupPrivateKey = group.groupPrivateKey;
    const lmsEncryptedGroupInfoKey = group.lmsEncryptedGroupInfoKey;
    const encryptedGroupName = group.name;
    const encGroupTypeWithSignatures = group.type;

    let baseId = creatorOrgName + creatorOrgMemberId;

    if (creatorMemberPublicKey) {
      // verify creatorMemberPublicKey
      await verifyMemberPublicKey(
        sessionId,
        creatorMemberPublicKey,
        Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.GROUP_OPERATION,
        group.trustedCreatingMemberPublicKeySignature,
        creatorOrgName,
        creatorOrgMemberId,
        group.creatorMemberStatus,
        "",
        "",
        group.creatorMemberOrgAdminPublicKey,
      );
    }

    // verify groupPublicKey Signature
    if (groupPublicKey) {
      if (!(await retrieveAndverifyLastSignature(sessionId, groupPublicKey))) {
        console.log("groupPublicKey verification failed.");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    }

    // decrypt Group Key
    encGroupKey = getMessageFromSignatureString(encGroupKey);
    const groupKey = decodeKey(
      await aeadDecryptWithAssociatedData(
        encGroupKey,
        baseId,
        asRawKey(await getFromStorage(sessionId, SharedPreferences.ORG_KEY)),
      ),
    );

    if (!groupKey) {
      console.log("GroupKey decryption failed.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    // decrypt Group Private Key
    const groupPrivateKey = await aeadDecryptWithAssociatedData(
      encGroupPrivateKey,
      baseId,
      asRawKey(await getFromStorage(sessionId, SharedPreferences.ORG_KEY)),
    );

    if (!groupPrivateKey) {
      console.log("groupPrivateKey decryption failed.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    // create lmsGroupKey
    const lmsGroupKey = await createLMSGroupKey(
      groupKey,
      creatorOrgName,
      creatorOrgMemberId,
    );

    // decrypt groupInfoKey using lmsGroupKey
    baseId = "lms" + creatorOrgName + creatorOrgMemberId;
    const groupInfoKey = decodeKey(
      await aeadDecryptWithAssociatedData(
        lmsEncryptedGroupInfoKey,
        baseId,
        lmsGroupKey,
      ),
    );

    // decrypt Group type and name using groupInfoKey
    baseId = creatorOrgName + creatorOrgMemberId;
    let groupName = "";
    if (encryptedGroupName) {
      // group name is optional, can be null
      groupName = await aeadDecryptWithAssociatedData(
        encryptedGroupName,
        baseId,
        groupInfoKey,
      );

      if (!groupName) {
        console.log("Group Name Decryption Failed");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }
    }

    // verify encryptedGroupType by creator member signing public key
    if (!group.creatorMemberSigningPublicKey) {
      console.log("creatorMemberSigningPublicKey not found");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }
    if (
      !(await verifyMessageForPublicKeyTag(
        sessionId,
        encGroupTypeWithSignatures,
        getMessageFromSignatureString(group.creatorMemberSigningPublicKey),
        SigningPublicKeyTags.GROUP_CREATOR_PUBLIC_KEY_TAG,
      ))
    ) {
      console.log("EncryptedGroupType verification failed.");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    // decrypt GroupType
    const encryptedGroupType = getMessageFromSignatureString(
      encGroupTypeWithSignatures,
    );
    const groupTypeString = await aeadDecryptWithAssociatedData(
      encryptedGroupType,
      baseId,
      groupInfoKey,
    );

    if (!groupTypeString) {
      console.log("Group Type Decryption Failed");
      throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
    }

    let groupType = null;
    if (groupTypeString == "Org-Public") {
      groupType = GroupType.PUBLIC;
    } else {
      groupType = GroupType.PRIVATE;
    }

    unjoinedGroupsCache.push({
      id: group.id,
      key: encodeKey(groupKey),
      signingPublicKey: groupPublicKey, // getMessageFromSignatureString(groupPublicKey),
      signingPrivateKey: groupPrivateKey,
      signedSigningPublicKey: groupPublicKey,
      creatorOrgName,
      creatorOrgMemberId,
      groupName: groupName,
      groupType: groupTypeString,
    });

    unjoinedGroupsResult.push({
      groupId: group.id,
      groupName: groupName,
      groupType: groupType,
    });
  }

  // save the unjoined groupKeys in cache
  await saveInStorage(
    sessionId,
    SharedPreferences.UNJOINED_GROUP_KEYS,
    JSON.stringify(unjoinedGroupsCache),
  );

  return unjoinedGroupsResult;
};

/**
 * This method joins a new public group.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId id of the group to join.
 */
export const joinNewPublicGroup = async (sessionId, groupId) => {
  if (groupId) {
    let groupInfo = await getGroupInfoFromCache(sessionId, groupId, false);

    let plainGroupKey = "";

    if (groupInfo) {
      plainGroupKey = groupInfo.key;
    }

    // If groupKey does not exist, call API to get orgKeyEncryptedGroupKey,
    // and decrypt it.
    if (!plainGroupKey) {
      groupInfo = await getOrgEncryptedGroupKey(sessionId, groupId);
      plainGroupKey = groupInfo.key;
    }

    // Encrypt groupKey with memberPublicKey
    const memberPublicKey = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_PUBLIC_KEY,
    );
    const memberSigningPrivateKey = await getMemberSigningPrivateKey(sessionId);
    const orgMemberId = await getFromStorage(
      sessionId,
      SharedPreferences.ORG_MEMBER_ID,
    );
    const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);
  const signingCurve = await getSigningCurveFromStorage(sessionId);

    const groupKeyEncryptionResponse = await encryptAsymmetric(
      encodeKey(asRawKey(plainGroupKey) || plainGroupKey),
      memberPublicKey,
      orgMemberId,
      encryptionCurve,
    );

    let memberPublicKeyEncryptedGroupKey = "";
    let memberPublicKeyEncryptedGroupKeyKek = "";

    if (groupKeyEncryptionResponse) {
      memberPublicKeyEncryptedGroupKey =
        groupKeyEncryptionResponse.encryptedText;
      memberPublicKeyEncryptedGroupKeyKek =
        groupKeyEncryptionResponse.keyEncryptionKey;
    }

    // sign memberPublicKeyEncryptedGroupKeyKek using member signing key
    const signedMemberKeyEncGroupKeyKEK = await signData(
      memberPublicKeyEncryptedGroupKeyKek,
      memberSigningPrivateKey,
      await getGroupKeyKekContext(sessionId, orgMemberId),
      signingCurve,
    );

    if (!signedMemberKeyEncGroupKeyKEK) {
      console.log("MemberKeyEncGroupKeyKEK Signature could not be generated.");
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    memberPublicKeyEncryptedGroupKeyKek = appendSignatureAndMetadata(
      memberPublicKeyEncryptedGroupKeyKek,
      signedMemberKeyEncGroupKeyKEK,
      SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
      await getGroupKeyKekContext(sessionId, orgMemberId),
    );

    // encrypt the groupSigningPrivateKey with memberPublicKey of member
    //TODO to be added
    const groupSigningPrivateKey = groupInfo.privateKey;

    const signingKeyEncryptionResponse = await encryptAsymmetric(
      groupSigningPrivateKey,
      memberPublicKey,
      orgMemberId,
      encryptionCurve,
    );

    let encryptedSigningPrivateKey = "";
    let encryptedSigningPrivateKeyKek = "";

    if (signingKeyEncryptionResponse) {
      encryptedSigningPrivateKey = signingKeyEncryptionResponse.encryptedText;
      encryptedSigningPrivateKeyKek =
        signingKeyEncryptionResponse.keyEncryptionKey;
    }

    // sign encryptedSigningPrivateKeyKek using member signing key
    const signedGroupPrivateKeyKek = await signData(
      encryptedSigningPrivateKeyKek,
      memberSigningPrivateKey,
      await getGroupKeyKekContext(sessionId, orgMemberId),
      signingCurve,
    );

    if (!signedGroupPrivateKeyKek) {
      console.log("signedGroupPrivateKeyKek Signature could not be generated.");
      throw BayunError.ERROR_AUTHENTICATION_FAILED;
    }

    encryptedSigningPrivateKeyKek = appendSignatureAndMetadata(
      encryptedSigningPrivateKeyKek,
      signedGroupPrivateKeyKek,
      SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
      await getGroupKeyKekContext(sessionId, orgMemberId),
    );

    if (
      memberPublicKeyEncryptedGroupKey &&
      memberPublicKeyEncryptedGroupKeyKek &&
      encryptedSigningPrivateKey &&
      encryptedSigningPrivateKeyKek
    ) {
      await joinPublicGroupAPI(
        sessionId,
        groupId,
        memberPublicKeyEncryptedGroupKey,
        memberPublicKeyEncryptedGroupKeyKek,
        encryptedSigningPrivateKey,
        encryptedSigningPrivateKeyKek,
        groupInfo.publicKey,
        plainGroupKey,
        groupInfo.creatorOrgMemberId,
        groupInfo.creatorOrgName,
      );
    } else {
      throw BayunError.ERROR_SOMETHING_WENT_WRONG;
    }
  } else {
    throw BayunError.ERROR_GROUP_ID_CANNOT_BE_NULL;
  }
};

/**
 * This function adds the given member to the given group, granting the user the keys for the group.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId Id of the group to which the user is to be added.
 * @param {String} orgMemberId OrgMemberId of the member to be added to the group.
 * @param {String} orgName Name of the org of the member to be added to the group.
 */
export const addNewParticipantToGroup = async (
  sessionId,
  groupId,
  orgMemberId,
  orgName,
) => {
  try {
    orgMemberId = orgMemberId.toLowerCase();
    orgName = orgName.toLowerCase();
    const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);
    const signingCurve = await getSigningCurveFromStorage(sessionId);

    await assertAddMemberInitiationAllowed(sessionId, orgName);
    // get the group key of the group in question
    const groupData = await getGroupKeyFromId(sessionId, groupId);

    const groupName = groupData.groupName;
    let signedMemberPubKey = null;

    // get the memberPublicKey of the member to be added.
    const memberPublicKeyOfMemberToAdd = await getMemberPublicKey(
      sessionId,
      orgMemberId,
      orgName,
      Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.GROUP_OPERATION,
    );

    if (memberPublicKeyOfMemberToAdd) {
      const memberSigningPrivateKey = await getMemberSigningPrivateKey(sessionId);

      // add trusted signature on member public key of the participant being added to the group
      const memberPubKey = getMessageFromSignatureString(
        memberPublicKeyOfMemberToAdd,
      );

      if (!memberPubKey) {
        console.log("Member Public Key not found");
        throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
      }

      //initially cheking localstorage for memberPublicKey (trusted signature), signing only if not found
      if (
        !(await getFromStorage(
          sessionId,
          orgMemberId + "kIsTrustedSignatureExists",
        ))
      ) {
        //signing member public key
        signedMemberPubKey = await signDataAndAppendSignatureWithMetadata(
          memberPubKey,
          memberSigningPrivateKey,
          await getMemberPublicKeyContext(orgName, orgMemberId),
          SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
          signingCurve,
        );
        if (!signedMemberPubKey) {
          console.log("MemberPublicKey Signature could not be generated.");
          throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
        }
      }
      // append signature metadata to the trusted signature on the member public key
      // of the participant being added to the group
      // let metadataObj = new SignatureMetadata();
      // metadataObj.context = await getMemberPublicKeyContext(sessionId);
      // metadataObj.signatureVerificationKey =
      //   SigningPublicKeyTags.MEMBER_PUBLIC_KEY_TAG;

      // let signatureMetadata = serializeThrift(metadataObj);
      // signedMemberPubKey = signedMemberPubKey + "$" + signatureMetadata;

      // add audit signature on the groupKey by member private of the participant adding
      // who is adding the new participant to the group
      let signedGroupKey = await signData(
        encodeKey(asRawKey(groupData.key) || groupData.key),
        memberSigningPrivateKey,
        await getGroupKeyContext(sessionId, groupName),
        signingCurve,
      );

      if (!signedGroupKey) {
        console.log("GroupKey Signature could not be generated.");
        throw BayunError.ERROR_AUTHENTICATION_FAILED;
      }

      // append signature metadata to the signature on the  group key of the group
      let metadataObj = new SignatureMetadata();
      metadataObj.context = await getGroupKeyContext(sessionId, groupName);
      metadataObj.signatureVerificationKey =
        SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG;

      let signatureMetadata = serializeThrift(metadataObj);
      signedGroupKey = signedGroupKey + "$" + signatureMetadata;

      // encrypt the group key with the memberPublicKey of member to be added
      const memberPublicKeyEncryptionResult = await encryptAsymmetric(
        encodeKey(asRawKey(groupData.key) || groupData.key),
        memberPublicKeyOfMemberToAdd,
        orgMemberId,
        encryptionCurve,
      );

      let memberPublicKeyEncryptedGroupKey = "";
      let memberPublicKeyEncryptedGroupKey_kek = "";

      if (memberPublicKeyEncryptionResult) {
        memberPublicKeyEncryptedGroupKey =
          memberPublicKeyEncryptionResult.encryptedText;
        memberPublicKeyEncryptedGroupKey_kek =
          memberPublicKeyEncryptionResult.keyEncryptionKey;
      }

      const groupSigningPublicKey = groupData.signedPublicKey;
      const groupSigningPrivateKey = groupData.privateKey;

      // sign groupPrivateKey with member signing key of the one adding participant
      let signedGroupPrivateKey = await signData(
        groupSigningPrivateKey,
        memberSigningPrivateKey,
        await getGroupPrivateKeyContext(sessionId, groupName),
        signingCurve,
      );

      if (!signedGroupPrivateKey) {
        console.log("GroupPrivateKey Signature could not be generated.");
        throw BayunError.ERROR_AUTHENTICATION_FAILED;
      }

      // append signature metadata to the signature on the  group private key of the group
      metadataObj = new SignatureMetadata();
      metadataObj.context = await getGroupPrivateKeyContext(
        sessionId,
        groupName,
      );
      metadataObj.signatureVerificationKey =
        SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG;

      signatureMetadata = serializeThrift(metadataObj);
      signedGroupPrivateKey = signedGroupPrivateKey + "$" + signatureMetadata;

      // encrypt the groupSigningPrivateKey with memberPrivateKey of the
      // member to be added
      let groupSigningPrivateKeyMemberEncrypted = "";
      let groupSigningPrivateKeyMemberEncrypted_kek = "";

      const groupSigningPrivateKeyMemberEncryptedResult =
        await encryptAsymmetric(
          groupSigningPrivateKey,
          memberPublicKeyOfMemberToAdd,
          orgMemberId,
          encryptionCurve,
        );

        if (groupSigningPrivateKeyMemberEncryptedResult) {
        groupSigningPrivateKeyMemberEncrypted =
          groupSigningPrivateKeyMemberEncryptedResult.encryptedText;
        groupSigningPrivateKeyMemberEncrypted_kek =
          groupSigningPrivateKeyMemberEncryptedResult.keyEncryptionKey;
      }

      // TODO: change this when using encryption types
      if (true) {
        // sign memberKeyEncryptedGroupKey_kek using member signing key
        const signedMemberKeyEncGroupKeyKEK = await signData(
          memberPublicKeyEncryptedGroupKey_kek,
          memberSigningPrivateKey,
          await getGroupKeyKekContext(sessionId, orgMemberId),
          signingCurve,
        );

        if (!signedMemberKeyEncGroupKeyKEK) {
          console.log("MemberKeyEncGroupKeyKEK Signature could not be generated.");
          throw BayunError.ERROR_AUTHENTICATION_FAILED;
        }

        memberPublicKeyEncryptedGroupKey_kek = appendSignatureAndMetadata(
          memberPublicKeyEncryptedGroupKey_kek,
          signedMemberKeyEncGroupKeyKEK,
          SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
          await getGroupKeyKekContext(sessionId, orgMemberId),
        );

        // sign group_private_key_kek using member signing key
        const signatureContext = await getGroupPrivateKeyKekContext(
          sessionId,
          orgName,
          orgMemberId,
        );

        const signedGroupPrivateKeyKek = await signData(
          groupSigningPrivateKeyMemberEncrypted_kek,
          memberSigningPrivateKey,
          signatureContext,
          signingCurve,
        );

        if (!signedGroupPrivateKeyKek) {
          console.log("GroupPrivateKeyKek Signature could not be generated.");
          throw BayunError.ERROR_AUTHENTICATION_FAILED;
        }

        groupSigningPrivateKeyMemberEncrypted_kek = appendSignatureAndMetadata(
          groupSigningPrivateKeyMemberEncrypted_kek,
          signedGroupPrivateKeyKek,
          SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
          signatureContext,
        );
      }

      // call the API to add the new participant
      await addNewParticipantToGroupApiCall(
        sessionId,
        orgMemberId,
        orgName,
        groupSigningPrivateKeyMemberEncrypted,
        groupSigningPrivateKeyMemberEncrypted_kek,
        memberPublicKeyEncryptedGroupKey,
        memberPublicKeyEncryptedGroupKey_kek,
        groupId,
        groupSigningPublicKey,
        signedMemberPubKey,
        signedGroupKey,
        signedGroupPrivateKey,
      );
    }
  } catch (error) {
    console.log(error);
    if (error.response) {
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    } else {
      throw error;
    }
  }
};

/**
 * This function adds the given list of members to the given group, granting the user the keys for the group.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId Id of the group to which the user is to be added.
 * @param {String} participantsToAddList list of objects of members to add, each object contains
 *                                  - orgMemberId and orgName.
 */
export const addNewParticipantsToGroup = async (
  sessionId,
  groupId,
  participantsToAddList,
) => {
  try {
    let membersFromOtherOrg = [];

    for (let i = 0; i < participantsToAddList.length; i++) {
      participantsToAddList[i].orgMemberId =
        participantsToAddList[i].orgMemberId.toLowerCase();
      participantsToAddList[i].orgName =
        participantsToAddList[i].orgName.toLowerCase();
    }

    if (await isOrgAdminApprovalPolicy(sessionId)) {
      const memberStatus = await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_STATUS,
      );
      if (memberStatus === Constants.MEMBER_STATUS.AUTO_APPROVED) {
        throw BayunError.AUTO_APPROVED_MEMBER_CANNOT_ADD_TO_GROUP;
      }

      const loggedInOrgName = (
        await getFromStorage(sessionId, SharedPreferences.ORG_NAME)
      )?.toLowerCase();
      const validParticipants = [];

      for (let i = 0; i < participantsToAddList.length; i++) {
        const participant = participantsToAddList[i];
        if (participant.orgName !== loggedInOrgName) {
          membersFromOtherOrg.push({
            orgMemberId: participant.orgMemberId,
            orgName: participant.orgName,
          });
        } else {
          validParticipants.push(participant);
        }
      }

      participantsToAddList = validParticipants;

      if (participantsToAddList.length === 0) {
        const errorList = [];
        if (membersFromOtherOrg.length > 0) {
          errorList.push({
            errorMessage: BayunError.CANNOT_ADD_MEMBER_FROM_OTHER_ORG,
            participantsList: membersFromOtherOrg,
          });
        }
        return {
          addedParticipantsCount: 0,
          addParticipantErrObject: errorList,
        };
      }
    }

    // get the group key of the group in question
    const groupData = await getGroupKeyFromId(sessionId, groupId);
    const encryptionCurve = await getEncryptionCurveFromStorage(sessionId);
  const signingCurve = await getSigningCurveFromStorage(sessionId);

    const groupName = groupData.groupName;

    // call the getMemberPublicKeys API
    const memberPublicKeysOfMembersToAdd = await getMemberPublicKeys(
      sessionId,
      participantsToAddList,
      Constants.MEMBER_PUBLIC_KEY_VERIFICATION_PURPOSE.GROUP_OPERATION,
    );

    //await logApiResponse("memberPublicKeysOfMembersToAdd",memberPublicKeysOfMembersToAdd);

    const groupSigningPublicKey = groupData.signedPublicKey;
    const groupSigningPrivateKey = groupData.privateKey;
    const memberSigningPrivateKey = await getMemberSigningPrivateKey(sessionId);

    //sign groupKey with member signing key of the one adding participant
    let signedGroupKey = await signData(
      encodeKey(asRawKey(groupData.key) || groupData.key),
      memberSigningPrivateKey,
      await getGroupKeyContext(sessionId, groupName),
      signingCurve,
    );

    if (!signedGroupKey) {
      console.log("GroupKey Signature could not be generated.");
      throw BayunError.ERROR_GROUP_KEY_SIGNATURE_FAILED;
    }

    // sign groupPrivateKey with member signing key of the one adding participant
    let signedGroupPrivateKey = await signData(
      groupSigningPrivateKey,
      memberSigningPrivateKey,
      await getGroupPrivateKeyContext(sessionId, groupName),
      signingCurve,
    );

    if (!signedGroupPrivateKey) {
      console.log("GroupPrivateKey Signature could not be generated.");
      throw BayunError.ERROR_GROUP_PRIVATE_KEY_SIGNATURE_FAILED;
    }

    const membersToAddRequest = [];

    let groupKeyKEKSignatureCouldNotBeGenerated = new Array();
    let groupPrivateKeyKEKSignatureCouldNotBeGenerated = new Array();
    let memberPublicKeySignatureCouldNotBeGenerated = new Array();
    let memberPublicKeyCouldNotFound = new Array();

    if (memberPublicKeysOfMembersToAdd.memberPublicKeyResponse) {
      for (
        let i = 0;
        i < memberPublicKeysOfMembersToAdd.memberPublicKeyResponse.length;
        i++
      ) {
        const memberPublicKeyToAdd =
          memberPublicKeysOfMembersToAdd.memberPublicKeyResponse[i];

        let orgMemberId = memberPublicKeyToAdd.orgMemberId.toLowerCase();
        let orgName = memberPublicKeyToAdd.orgName.toLowerCase();
        let signedMemberPubKey = null;
        let participantObject = {};
        participantObject["orgMemberId"] = orgMemberId;
        participantObject["orgName"] = orgName;

        // add trusted signature on member public key of the participant being added to the group
        const memberPubKey = getMessageFromSignatureString(
          memberPublicKeyToAdd.publicKey,
        );

        if (!memberPubKey) {
          console.log("Member Public Key not found");
          memberPublicKeyCouldNotFound.push(participantObject);
          continue;
          //throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
        }

        //initially cheking localstorage for memberPublicKey (trusted signature), signing only if not found
        if (
          !(await getFromStorage(
            sessionId,
            orgMemberId + "kIsTrustedSignatureExists",
          ))
        ) {
          //signing memberPublicKey
          signedMemberPubKey = await signDataAndAppendSignatureWithMetadata(
            memberPubKey,
            memberSigningPrivateKey,
            await getMemberPublicKeyContext(orgName, orgMemberId),
            SigningPublicKeyTags.MEMBER_SIGNING_PUBLIC_KEY_TAG,
            signingCurve,
          );
          if (!signedMemberPubKey) {
            memberPublicKeySignatureCouldNotBeGenerated.push(participantObject);
            continue;
            // console.log("MemberPublicKey Signature could not be generated.");
            // throw BayunError.ERROR_MEMBER_VERIFICATION_FAILED;
          }
        }

        // append signature metadata to the trusted signature on the member public key
        // of the participant being added to the group
        // let metadataObj = new SignatureMetadata();
        // metadataObj.context = await getMemberPublicKeyContext(sessionId);
        // metadataObj.signatureVerificationKey =
        //   SigningPublicKeyTags.MEMBER_PUBLIC_KEY_TAG;

        // let signatureMetadata = serializeThrift(metadataObj);
        // signedMemberPubKey = signedMemberPubKey + "$" + signatureMetadata;

        // add audit signature on the groupKey by member private of the participant adding
        // who is adding the new participant to the group

        // append signature metadata to the signature on the  group key of the group
        let metadataObj = new SignatureMetadata();
        metadataObj.context = await getGroupKeyContext(sessionId, groupName);
        metadataObj.signatureVerificationKey =
          SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG;

        let signatureMetadata = serializeThrift(metadataObj);
        const participantSignedGroupKey =
          signedGroupKey + "$" + signatureMetadata;

        // encrypt the group key with the memberPublicKey of member to be added
        const memberPublicKeyEncryptionResult = await encryptAsymmetric(
          encodeKey(asRawKey(groupData.key) || groupData.key),
          getMessageFromSignatureString(memberPublicKeyToAdd.publicKey),
          orgMemberId,
          encryptionCurve,
        );

        let memberPublicKeyEncryptedGroupKey = "";
        let memberPublicKeyEncryptedGroupKey_kek = "";

        if (memberPublicKeyEncryptionResult) {
          memberPublicKeyEncryptedGroupKey =
            memberPublicKeyEncryptionResult.encryptedText;
          memberPublicKeyEncryptedGroupKey_kek =
            memberPublicKeyEncryptionResult.keyEncryptionKey;
        }

        // append signature metadata to the signature on the  group private key of the group
        metadataObj = new SignatureMetadata();
        metadataObj.context = await getGroupPrivateKeyContext(
          sessionId,
          groupName,
        );
        metadataObj.signatureVerificationKey =
          SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG;

        signatureMetadata = serializeThrift(metadataObj);
        const participantSignedGroupPrivateKey =
          signedGroupPrivateKey + "$" + signatureMetadata;

        // encrypt the groupSigningPrivateKey with memberPrivateKey of the
        // member to be added
        let groupSigningPrivateKeyMemberEncrypted = "";
        let groupSigningPrivateKeyMemberEncrypted_kek = "";

        const groupSigningPrivateKeyMemberEncryptedResult =
          await encryptAsymmetric(
            groupSigningPrivateKey,
            getMessageFromSignatureString(memberPublicKeyToAdd.publicKey),
          orgMemberId,
          encryptionCurve,
        );

        if (groupSigningPrivateKeyMemberEncryptedResult) {
          groupSigningPrivateKeyMemberEncrypted =
            groupSigningPrivateKeyMemberEncryptedResult.encryptedText;
          groupSigningPrivateKeyMemberEncrypted_kek =
            groupSigningPrivateKeyMemberEncryptedResult.keyEncryptionKey;
        }

        //TODO: change this when using encryption types
        if (true) {
          // sign memberKeyEncryptedGroupKey_kek using member signing key
          const signedMemberKeyEncGroupKeyKEK = await signData(
            memberPublicKeyEncryptedGroupKey_kek,
            memberSigningPrivateKey,
            await getGroupKeyKekContext(sessionId, orgMemberId),
            signingCurve,
          );

          //if GroupKeyKEK Signature could not be generated.
          if (!signedMemberKeyEncGroupKeyKEK) {
            //if(true){
            console.log(
              "MemberKeyEncGroupKeyKEK Signature could not be generated.",
            );
            groupKeyKEKSignatureCouldNotBeGenerated.push(participantObject);
            continue;
            //throw BayunError.ERROR_AUTHENTICATION_FAILED;
          }

          memberPublicKeyEncryptedGroupKey_kek = appendSignatureAndMetadata(
            memberPublicKeyEncryptedGroupKey_kek,
            signedMemberKeyEncGroupKeyKEK,
            SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
            await getGroupKeyKekContext(sessionId, orgMemberId),
          );

          // sign group_private_key_kek using member signing key
          const signatureContext = await getGroupPrivateKeyKekContext(
            sessionId,
            orgName,
            orgMemberId,
          );

          const signedGroupPrivateKeyKek = await signData(
            groupSigningPrivateKeyMemberEncrypted_kek,
            memberSigningPrivateKey,
            signatureContext,
            signingCurve,
          );

          //if GroupPrivateKeyKEK Signature could not be generated.
          if (!signedGroupPrivateKeyKek) {
            console.log("GroupPrivateKeyKek Signature could not be generated.");
            groupPrivateKeyKEKSignatureCouldNotBeGenerated.push(
              participantObject,
            );
            continue;
            //throw BayunError.ERROR_AUTHENTICATION_FAILED;
          }

          groupSigningPrivateKeyMemberEncrypted_kek =
            appendSignatureAndMetadata(
              groupSigningPrivateKeyMemberEncrypted_kek,
              signedGroupPrivateKeyKek,
              SigningPublicKeyTags.MEMBER_ADDING_PUBLIC_KEY_TAG,
              signatureContext,
            );
        }

        // TODO: make req object here
        membersToAddRequest.push({
          orgMemberId: orgMemberId,
          orgName: orgName,
          memberEncryptedGroupPrivateKey: groupSigningPrivateKeyMemberEncrypted,
          memberEncryptedGroupPrivateKey_kek:
            groupSigningPrivateKeyMemberEncrypted_kek,
          memberKeyEncryptedGroupKey: memberPublicKeyEncryptedGroupKey,
          memberKeyEncryptedGroupKey_kek: memberPublicKeyEncryptedGroupKey_kek,
          groupPublicKey: groupSigningPublicKey,
          signedMemberPublicKey: signedMemberPubKey,
          signedGroupKey: participantSignedGroupKey,
          signedGroupPrivateKey: participantSignedGroupPrivateKey,
        });
      }

      // call the API to add the new participant
      //receive
      let addNewParticipantsToGroupApiCallResponse =
        await addNewParticipantsToGroupApiCall(sessionId, {
          groupId,
          participantsRequest: membersToAddRequest,
        });

      //declearing the possible errors for members
      let errorMessage = [
        "GroupKeyKEK Signature could not be generated.",
        "GroupPrivateKeyKEK Signature could not be generated.",
        "MemberPublicKey Signature could not be generated.", //to be verified
        "Member Public Key could not be verified",
      ];
      let participantsList = [
        groupKeyKEKSignatureCouldNotBeGenerated,
        groupPrivateKeyKEKSignatureCouldNotBeGenerated,
        memberPublicKeySignatureCouldNotBeGenerated,
        memberPublicKeyCouldNotFound,
      ];

      // adding errors from getMemberPublicKeys
      var errorList = memberPublicKeysOfMembersToAdd.AddParticipantErrObject;

      if (membersFromOtherOrg.length > 0) {
        errorList.push({
          errorMessage: BayunError.CANNOT_ADD_MEMBER_FROM_OTHER_ORG,
          participantsList: membersFromOtherOrg,
        });
      }

      // adding errors from addNewParticipantsToGroupApiCallResponse
      if (
        addNewParticipantsToGroupApiCallResponse.data.participantsNotAdded !=
        null
      ) {
        Array.prototype.push.apply(
          errorList,
          addNewParticipantsToGroupApiCallResponse.data.participantsNotAdded,
        );
      }

      // adding errors of addNewParticipantsToGroup
      for (let i = 0; i < errorMessage.length; i++) {
        if (participantsList[i].length != 0) {
          const errorObject = {};
          errorObject["errorMessage"] = errorMessage[i];
          errorObject["participantsList"] = participantsList[i];
          errorList.push(errorObject);
        }
      }

      let addMemnersResponse = {};
      addMemnersResponse["addedParticipantsCount"] =
        addNewParticipantsToGroupApiCallResponse.data.addedParticipantsCount;
      addMemnersResponse["addParticipantErrObject"] = errorList;

      return addMemnersResponse;
    }
  } catch (error) {
    console.log(error);
    if (error.response) {
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    } else {
      throw error;
    }
  }
};

/**
 * This function lets the logged in user leave the group associated with the given groupId.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} groupId The id of the group to leave.
 */
export const leaveGroupById = async (sessionId, groupId) => {
  try {
    await leaveGroup(sessionId, groupId);
  } catch (error) {
    console.log(error);
    if (error.response) {
      throw getErrorMessage(sessionId, error?.response?.data ?? error);
    } else {
      throw error;
    }
  }

  // remove the groupKey in the cache
  const savedGroupKeysString = await getFromStorage(
    sessionId,
    SharedPreferences.JOINED_GROUP_KEYS,
  );
  let savedGroupKeys = "";

  if (savedGroupKeysString) {
    savedGroupKeys = JSON.parse(savedGroupKeysString);
  }

  let index = -1;
  if (savedGroupKeys) {
    for (let i = 0; i < savedGroupKeys.length; i++) {
      const group = savedGroupKeys[i];
      if (group.id === groupId) {
        index = i;
        break;
      }
    }
  }

  if (index > -1) {
    console.log("Deleting the key from cache.");
    savedGroupKeys.splice(index, 1);
  }

  await saveInStorage(
    sessionId,
    SharedPreferences.JOINED_GROUP_KEYS,
    JSON.stringify(savedGroupKeys),
  );

  let encryptionCount = await getFromStorage(
    sessionId,
    SharedPreferences.ENCRYPTION_COUNT,
  );
  let decryptionCount = await getFromStorage(
    sessionId,
    SharedPreferences.DECRYPTION_COUNT,
  );

  if (encryptionCount || decryptionCount) {
    await updateAndSaveStatistics(sessionId, true, false, false, true);
  }
};

/**
 * Searches cache for group info of the group with given groupId.
 *
 * @param {String} groupId id of the group whose key is to be returned.
 * @returns info of the group corresponding to the groupId
 */
async function getGroupInfoFromCache(sessionId, groupId, isJoinedGroup) {
  let savedGroupKeysString = "";

  if (isJoinedGroup) {
    savedGroupKeysString = await getFromStorage(
      sessionId,
      SharedPreferences.JOINED_GROUP_KEYS,
    );
  } else {
    savedGroupKeysString = await getFromStorage(
      sessionId,
      SharedPreferences.UNJOINED_GROUP_KEYS,
    );
  }

  if (savedGroupKeysString) {
    const savedGroupKeys = JSON.parse(savedGroupKeysString);
    // check if the cache has the key for the given groupId
    if (savedGroupKeys) {
      for (let i = 0; i < savedGroupKeys.length; i++) {
        const group = savedGroupKeys[i];
        if (group.id === groupId) {
          return {
            key: asRawKey(group.key),
            publicKey: group.signingPublicKey,
            privateKey: group.signingPrivateKey,
            signedPublicKey: group.signedSigningPublicKey,
            creatorOrgName: group.creatorOrgName,
            creatorOrgMemberId: group.creatorOrgMemberId,
            groupName: group.groupName,
          };
        }
      }
      throw BayunError.ERROR_GROUP_DOES_NOT_EXIST;
    }
  }
  return null;
}
